import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Download, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import MfaCodeInput from './MfaCodeInput';
import {
    parseMfaError,
    clockSkewSeconds,
    describeSkew,
    EXPIRED_SESSION_CODES,
    STALE_ENROLLMENT_CODES,
} from '../../lib/mfaErrors';

/**
 * MfaEnrollment — TOTP enrollment flow (plan §8.2 / §12.2).
 *
 * Used by BOTH the forced-at-login path (Login.jsx, pending-token session) and
 * Settings → Security (full Bearer session). The caller supplies the axios
 * headers so the same component works with either credential kind:
 *   - pending token:  { Authorization: `Bearer ${mfa_token}` }
 *   - full session:   getAuthHeader()
 *
 * Steps: QR + manual key → code entry → recovery codes (with a save gate).
 * Recovery codes are shown ONCE; "Continue" stays disabled until the user
 * confirms they saved them.
 *
 * From the login path, `/auth/mfa/enable` returns the session alongside the
 * recovery codes (#594), so `onComplete` receives the whole response and the
 * caller signs the user in. From Settings the response carries only the codes
 * and the existing session is left alone — `onComplete` must tolerate both.
 */
export default function MfaEnrollment({ api, headers, onComplete }) {
    const [step, setStep] = useState('qr'); // 'qr' | 'codes'
    const [setup, setSetup] = useState(null); // { otpauth_uri, secret_masked, expires_at, server_time, resumed }
    const [code, setCode] = useState('');
    const [recoveryMode, setRecoveryMode] = useState(false);
    const [error, setError] = useState(null);
    const [fatalError, setFatalError] = useState(null);
    const [clockSkew, setClockSkew] = useState(null);
    const [enabling, setEnabling] = useState(false);
    const [result, setResult] = useState(null);
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);
    const [remainingMs, setRemainingMs] = useState(null);

    const recoveryCodes = result?.recovery_codes || [];

    const start = React.useCallback(async (rotate = false) => {
        setError(null);
        setFatalError(null);
        try {
            const res = await api.post(
                `/auth/mfa/setup${rotate ? '?rotate=true' : ''}`,
                {},
                { headers },
            );
            setSetup(res.data);
            // Warn BEFORE the user types anything — the flow is clock-sensitive
            // and nothing used to measure it (#594 D6).
            setClockSkew(clockSkewSeconds(res.data.server_time));
        } catch (err) {
            const parsed = parseMfaError(err, 'Could not start enrollment. Please try again.');
            if (EXPIRED_SESSION_CODES.has(parsed.code)) setFatalError(parsed.message);
            else setError(parsed.message);
        }
        // `headers` is a fresh object literal on every render in both callers,
        // so it must stay out of the dep array or this would re-fetch forever.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    // D3: setup must fire ONCE per mounted enrollment. The app renders under
    // <React.StrictMode> (src/index.js), which double-invokes effects in
    // development — two /auth/mfa/setup calls would race, and because setup used
    // to mint a new seed every time, the QR rendered and the seed persisted need
    // not have agreed. Same guard shape as PersonalInboxSurface's
    // syncAttemptedRef ("survives StrictMode double-invoke").
    const setupStartedRef = React.useRef(false);
    React.useEffect(() => {
        if (setupStartedRef.current) return;
        setupStartedRef.current = true;
        start();
    }, [start]);

    // Countdown for the enrollment window, so "how long have I got?" is visible
    // rather than discovered by failing.
    React.useEffect(() => {
        if (!setup?.expires_at) return undefined;
        const expiresAt = Date.parse(setup.expires_at);
        if (Number.isNaN(expiresAt)) return undefined;
        const tick = () => setRemainingMs(Math.max(0, expiresAt - Date.now()));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [setup?.expires_at]);

    const enable = async (submitted) => {
        const theCode = (submitted ?? code).trim();
        if (!theCode) return;
        setEnabling(true);
        setError(null);
        try {
            const res = await api.post('/auth/mfa/enable', { code: theCode }, { headers });
            setResult(res.data);
            setStep('codes');
        } catch (err) {
            const parsed = parseMfaError(err);
            setCode('');
            if (parsed.code === 'clock_skew' && parsed.skewSeconds !== null) {
                setClockSkew(parsed.skewSeconds);
            }
            if (EXPIRED_SESSION_CODES.has(parsed.code)) {
                // Not a code error — the session behind the screen is gone, and
                // rendering it under the code boxes is what made D5 look like a
                // rejected code.
                setFatalError(parsed.message);
            } else if (STALE_ENROLLMENT_CODES.has(parsed.code)) {
                // The QR on screen is dead. Replace it rather than leaving the
                // user to guess why a correct code keeps failing.
                setError(parsed.message);
                setSetup(null);
                start(true);
            } else {
                setError(parsed.message);
            }
        } finally {
            setEnabling(false);
        }
    };

    const copyKey = async () => {
        try {
            await navigator.clipboard.writeText((setup?.secret_masked || '').replace(/\s/g, ''));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError('Could not copy — select the key manually.');
        }
    };

    const copyAll = async () => {
        try {
            await navigator.clipboard.writeText(recoveryCodes.join('\n'));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError('Could not copy — download the file instead.');
        }
    };

    const download = () => {
        const blob = new Blob(
            [`NurtureCRM recovery codes\nEach code works once.\n\n${recoveryCodes.join('\n')}\n`],
            { type: 'text/plain' }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'nurturecrm-recovery-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (step === 'codes') {
        return (
            <div className="space-y-5" data-testid="mfa-recovery-codes-screen">
                <div>
                    <h3 className="text-lg font-semibold text-stone-900">Save your recovery codes</h3>
                    <p className="text-sm text-stone-500 mt-1">
                        Each code works once if you lose your phone. We can't show these again.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto" data-testid="mfa-recovery-codes">
                    {recoveryCodes.map((c) => (
                        <code key={c} className="text-sm font-mono text-stone-800 bg-stone-50 border border-stone-200 rounded px-2 py-1 text-center">
                            {c}
                        </code>
                    ))}
                </div>
                <div className="flex justify-center gap-3">
                    <button
                        type="button"
                        onClick={copyAll}
                        data-testid="mfa-copy-codes-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700"
                    >
                        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied' : 'Copy all'}
                    </button>
                    <button
                        type="button"
                        onClick={download}
                        data-testid="mfa-download-codes-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700"
                    >
                        <Download className="w-4 h-4" />
                        Download .txt
                    </button>
                </div>
                <label className="flex items-center justify-center gap-2 cursor-pointer" data-testid="mfa-saved-gate">
                    <input
                        type="checkbox"
                        checked={saved}
                        onChange={(e) => setSaved(e.target.checked)}
                        className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20"
                    />
                    <span className="text-sm text-stone-700">I've saved these somewhere safe</span>
                </label>
                <div className="flex justify-center">
                    <button
                        type="button"
                        disabled={!saved}
                        onClick={() => onComplete?.(result)}
                        data-testid="mfa-continue-btn"
                        className="px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {result?.access_token ? 'Continue to NurtureCRM' : 'Continue'}
                    </button>
                </div>
                {result?.access_token && (
                    <p className="text-xs text-emerald-700 text-center" data-testid="mfa-signed-in-note">
                        Two-step verification is on. You're signed in.
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="mfa-enrollment-qr-step">
            <div>
                <h3 className="text-lg font-semibold text-stone-900">Set up two-step verification</h3>
                <p className="text-sm text-stone-500 mt-1">
                    Scan this code with your authenticator app, then enter the code it shows.
                </p>
            </div>

            {fatalError && (
                <p data-testid="mfa-session-error" className="text-sm text-red-600 text-center">
                    {fatalError}
                </p>
            )}

            {error && !fatalError && (
                <p data-testid="mfa-setup-error" className="text-sm text-red-600 text-center">
                    {error}
                </p>
            )}

            {clockSkew !== null && (
                <p
                    data-testid="mfa-clock-skew-warning"
                    className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                        Your device clock is {describeSkew(clockSkew)}. Turn on automatic date &amp;
                        time, or codes may be rejected.
                    </span>
                </p>
            )}

            {!setup ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                </div>
            ) : (
                <>
                    <div className="flex flex-col sm:flex-row items-center gap-6 justify-center">
                        <div className="bg-white p-3 rounded-xl border border-stone-200" data-testid="mfa-qr">
                            <QRCodeSVG value={setup.otpauth_uri} size={160} />
                        </div>
                        <div className="text-center sm:text-left">
                            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">
                                Can't scan?
                            </p>
                            <p className="text-xs text-stone-500 mb-2">Enter this key manually:</p>
                            <code
                                data-testid="mfa-manual-key"
                                className="block text-sm font-mono text-stone-800 bg-stone-50 border border-stone-200 rounded px-2 py-1 mb-2"
                            >
                                {setup.secret_masked}
                            </code>
                            <button
                                type="button"
                                onClick={copyKey}
                                data-testid="mfa-copy-key-btn"
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-700"
                            >
                                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <MfaCodeInput
                        value={code}
                        onChange={setCode}
                        onSubmit={enable}
                        error={fatalError ? null : error}
                        disabled={enabling}
                        recoveryMode={recoveryMode}
                        onToggleRecovery={setRecoveryMode}
                    />

                    {remainingMs !== null && (
                        <p className="text-xs text-stone-500 text-center" data-testid="mfa-setup-countdown">
                            {remainingMs > 0
                                ? `This setup expires in ${Math.floor(remainingMs / 60000)}:${String(
                                      Math.floor((remainingMs % 60000) / 1000),
                                  ).padStart(2, '0')}`
                                : 'This setup has expired — get a new QR code below.'}
                        </p>
                    )}

                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={() => enable()}
                            disabled={enabling || !code.trim()}
                            data-testid="mfa-enable-btn"
                            className="px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {enabling ? 'Turning on…' : 'Turn on'}
                        </button>
                    </div>

                    {/* Rotation is now a deliberate act instead of an invisible
                        side effect of navigating away and back (#594 D3). */}
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={() => { setCode(''); setSetup(null); start(true); }}
                            data-testid="mfa-rotate-btn"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-700"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Show me a new QR code
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
